import { CurrencyPipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CoinflipService } from '../coinflip.service';
import { ModalService } from '@app/modal/modal.service';
import { OfferSentModalComponent, TradeSentType } from '@app/modals/offer-sent-modal/offer-sent-modal.component';
import { NotificationService } from '@app/notification/notification.service';
import { COINFLIP_SIDE_BLUE, COINFLIP_SIDE_RED, Coinflip } from '@models/coinflip.interface';
import { Item } from '@models/item.interface';
import { CoinflipCoinComponent } from '@shared/coinflip-coin/coinflip-coin.component';
import { ToggleDropdownDirective } from '@shared/directives/toggle-dropdown.directive';
import { DropdownItemComponent } from '@shared/dropdown/dropdown-item.component';
import { DropdownComponent } from '@shared/dropdown/dropdown.component';
import { IconComponent } from '@shared/icon/icon.component';
import { ItemListComponent } from '@shared/item-list/item-list.component';
import { ModalBoxComponent } from '@shared/modal-box/modal-box.component';
import { SessionService } from '@shared/session.service';
import { SliderComponent } from '@shared/slider/slider.component';
import { UIService } from '@shared/ui.service';
import { findBestCombination } from '@utils';

@Component({
  selector: 'app-coinflip-deposit-modal',
  templateUrl: './coinflip-deposit-modal.component.html',
  styleUrls: ['./coinflip-deposit-modal.component.scss'],
  imports: [
    ModalBoxComponent,
    FormsModule,
    IconComponent,
    ToggleDropdownDirective,
    DropdownComponent,
    DropdownItemComponent,
    SliderComponent,
    ItemListComponent,
    CoinflipCoinComponent,
    CurrencyPipe,
  ],
})
export class CoinflipDepositModalComponent implements OnInit {
  selectedItems: Item[] = [];
  items: Item[] = [];
  sort = 'high-low';
  sortName: Record<string, string> = {
    'high-low': 'Highest Price',
    'low-high': 'Lowest Price',
  };
  coinflip: Coinflip;
  depositType: 'create' | 'join' = 'create';
  totalValue = 0;
  selectedValue = 0;
  difference: number = 10;
  search = '';
  loading = true;
  readonly blueSide = COINFLIP_SIDE_BLUE;
  readonly redSide = COINFLIP_SIDE_RED;

  constructor(
    public session: SessionService,
    private modalService: ModalService,
    public coinflipService: CoinflipService,
    private notificationService: NotificationService,
    private uiService: UIService,
  ) {
    this.depositType = this.modalService.data.depositType;
    if (this.depositType === 'create') {
      this.coinflipService.coinSide = COINFLIP_SIDE_BLUE;
    } else {
      this.coinflip = this.modalService.data.coinflip;
      // Joining always takes the side the host left open.
      this.coinflipService.coinSide = this.coinflip?.host?.side === COINFLIP_SIDE_BLUE ? COINFLIP_SIDE_RED : COINFLIP_SIDE_BLUE;
    }
  }

  get modalMaxWidth(): number {
    return this.uiService.screenSize === 'XLarge' ? 1400 : 875;
  }

  async ngOnInit() {
    this.loading = true;
    try {
      this.depositType = this.modalService.data.depositType;
      if (this.depositType === 'join') {
        this.coinflip = this.modalService.data.coinflip;
      }
      await this.session.loadInventory('coinflip', true);
      this.updateItems();
    } catch {
      this.loading = false;
    } finally {
      this.loading = false;
    }
  }

  onFilterChange() {
    this.session.filterInventory(this.search);
    this.items = this.session.filteredInventory;
  }

  updateItems() {
    this.selectedItems = [];
    let allItems = structuredClone(this.session.inventory);

    if (this.depositType === 'join' && this.coinflip) {
      allItems = allItems.filter((item) => item.price <= this.coinflip.max_val && item.price >= this.coinflip.min_val);
    }

    this.items = allItems;

    this.totalValue = this.items.reduce((acc, item) => acc + item.price, 0);
  }

  isItemEligible(item: Item): boolean {
    if (this.depositType !== 'join' || !this.coinflip) return true;

    if (this.selectedItems.some((i) => i.assetid === item.assetid)) return true;

    const currentTotal = this.selectedItems.reduce((acc, i) => acc + i.price, 0);
    const newTotal = currentTotal + item.price;

    return newTotal <= this.coinflip.max_val;
  }

  async refresh() {
    this.loading = true;
    await this.session.loadInventory('coinflip');
    this.updateItems();
    this.loading = false;
  }

  onSortChange(sort: string) {
    this.sort = sort;
    this.loading = false;
  }

  pickSide(side: number) {
    if (this.depositType === 'join' && this.coinflip && this.coinflip.host?.side === side) {
      return;
    }

    this.coinflipService.coinSide = side;
  }

  autoSelect() {
    const items = findBestCombination(structuredClone(this.items), this.coinflip.min_val, this.coinflip.max_val, 20);

    if (!items) {
      this.notificationService.error('Could not find any items that match the requirement value.', 'ERROR');
      return;
    }

    this.selectedItems = items;
  }

  async deposit() {
    if (this.selectedItems.length === 0) {
      this.notificationService.error('Please select items to deposit', 'ERROR');
      return;
    }

    this.loading = true;
    try {
      let response;
      if (this.depositType === 'create') {
        response = (await this.coinflipService.createCoinflip(this.selectedItems, this.coinflipService.coinSide, this.difference)) as {
          offerid: number;
          skins: Item[];
          hash: string;
        };
      } else if (this.depositType === 'join') {
        response = (await this.coinflipService.joinCoinflip(this.coinflip, this.selectedItems)) as {
          offerid: number;
          skins: Item[];
          hash: string;
        };
      }

      this.modalService.open(OfferSentModalComponent, {
        type: TradeSentType.Deposit,
        offerid: response.offerid,
        hash: response.hash,
      });
    } catch {
      // The socket layer already toasts the rejection; catching only stops it escaping as an unhandled rejection.
      return;
    } finally {
      this.loading = false;
    }
  }
}
